import * as React from 'react';
import { ReactElement, useState } from 'react';

import { del } from '../../api/utils';
import Icon from '../common/Icon';
import SearchAndSelect from '../search/SearchAndSelect';
import { ISearchResult } from '../search/SearchBar';

interface IUser {
    id: number;
    label: string;
    link: string;
}

interface IExtraPermission {
    id: number;
    removeURI: string;
    user: IUser;
}

interface IProps {
    addExtraPermissionURI: string;
    csrfToken: string;
    searchURI: string;
    users: IExtraPermission[];
}

interface IExtraPermissionWithAdding extends IExtraPermission {
    adding?: boolean;
}

export default function ExtraCurriculumAccess({
    addExtraPermissionURI,
    csrfToken,
    searchURI,
    users: initialUsers,
}: IProps): ReactElement {
    const [users, setUsers] = useState<IExtraPermissionWithAdding[]>(initialUsers);
    const [loading, setLoading] = useState<boolean>(false);

    const onSelectUser = async (item: ISearchResult): Promise<void> => {
        const tempItem: IExtraPermissionWithAdding = {
            id: -item.id,
            removeURI: '',
            user: {
                id: item.id,
                label: item.label,
                link: `/users/${item.id}`,
            },
            adding: true,
        };
        setUsers((prev) => [...prev, tempItem]);
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('authenticity_token', csrfToken);
            formData.append('user_id', String(item.id));
            const response = await fetch(addExtraPermissionURI, {
                body: formData,
                headers: { Accept: 'application/json' },
                method: 'POST',
            });
            const data = await response.json().catch(() => ({}));
            if (data.success) {
                setUsers((prev) =>
                    prev.map((u) =>
                        u.id === tempItem.id
                            ? {
                                  id: data.id,
                                  removeURI: data.removeURI,
                                  user: data.user,
                              }
                            : u,
                    ),
                );
            } else {
                setUsers((prev) => prev.filter((u) => u.id !== tempItem.id));
                alert(data.error || 'Failed to add user');
            }
        } catch (err) {
            setUsers((prev) => prev.filter((u) => u.id !== tempItem.id));
            alert(err instanceof Error ? err.message : 'Failed to add user');
        } finally {
            setLoading(false);
        }
    };

    const onRemove = async (cep: IExtraPermissionWithAdding): Promise<void> => {
        const removed = { ...cep };
        setUsers((prev) => prev.filter((u) => u.id !== cep.id));
        try {
            await del(cep.removeURI, csrfToken);
        } catch (err) {
            setUsers((prev) => [...prev, removed]);
            alert(err instanceof Error ? err.message : 'Failed to remove');
        }
    };

    return (
        <div>
            <div className="mb-4">
                <h5>Add user</h5>
                <div className="d-flex align-items-center gap-2">
                    <div style={{ minWidth: 300 }}>
                        <SearchAndSelect
                            key={users.length}
                            csrfToken={csrfToken}
                            model="users"
                            onSelect={onSelectUser}
                            placeholder="Search for a user..."
                            searchURI={searchURI}
                            selectedItem={null}
                        />
                    </div>
                    {loading && <span className="text-muted">Adding...</span>}
                </div>
            </div>

            <h5>Users with extra access</h5>
            {users.length === 0 ? (
                <p className="text-muted">No users with extra access yet.</p>
            ) : (
                <table className="table table-striped">
                    <thead>
                        <tr>
                            <th>User</th>
                            <th style={{ width: 120 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((cep) => (
                            <tr key={cep.adding ? `temp-${cep.user.id}` : cep.id}>
                                <td>
                                    <a href={cep.user.link}>{cep.user.label}</a>
                                    <span className="text-muted ms-2">
                                        (#{cep.user.id})
                                    </span>
                                </td>
                                <td>
                                    {cep.adding ? (
                                        <span className="text-muted">
                                            Adding...
                                        </span>
                                    ) : (
                                        <button
                                            className="btn btn-default btn-sm"
                                            onClick={(): void => {
                                                onRemove(cep);
                                            }}
                                            title="Remove extra access"
                                            type="button"
                                        >
                                            <Icon
                                                fixedWidth
                                                name="times"
                                                text="Remove"
                                            />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}
